import React, { useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L, { Icon } from 'leaflet';
import { PhoneIcon, MapPinIcon, GlobeAltIcon, SparklesIcon } from '@heroicons/react/24/outline';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// @ts-ignore
delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

interface Store {
  id: number;
  city: string;
  name: string;
  address: string;
  postalCode: string;
  phone?: string;
  website?: string;
  lat: number;
  lng: number;
  country: string;
}

const stores: Store[] = [
  {
    id: 1,
    city: 'ARLES',
    name: 'Bijouterie ESCANEZ',
    address: '34/36, rue des Suisses',
    postalCode: '13200 ARLES',
    phone: '04.90.96.07.64',
    website: 'https://www.bijouterie-horlogerie-escanez.fr/',
    lat: 43.6772,
    lng: 4.6278,
    country: 'France',
  },
  {
    id: 2,
    city: 'AVIGNON',
    name: 'CARLINE',
    address: '11 rue de la République',
    postalCode: '84000 AVIGNON',
    phone: '04.90.82.67.48',
    lat: 43.9493,
    lng: 4.8055,
    country: 'France',
  },
  {
    id: 3,
    city: 'FONTVIEILLE',
    name: 'LESPRIT DECO 57',
    address: '3 av. des Moulins',
    postalCode: '13990 FONTVIEILLE',
    phone: '',
    website: 'https://lespritdeco57.com/',
    lat: 43.7272,
    lng: 4.7094,
    country: 'France',
  },
  {
    id: 4,
    city: 'NANTES',
    name: 'CIGOIRE',
    address: '3 rue Scribe',
    postalCode: '44000 NANTES',
    phone: '',
    lat: 47.2184,
    lng: -1.5536,
    country: 'France',
  },
  {
    id: 5,
    city: 'PARIS',
    name: 'PRINTEMPS HAUSSMANN',
    address: '64 Bd Haussmann – rdc Printemps joaillier – Espace Créateurs',
    postalCode: '75009 Paris',
    phone: '',
    website: 'https://www.printemps.com/fr/fr',
    lat: 48.8738,
    lng: 2.3282,
    country: 'France',
  },
  {
    id: 6,
    city: 'TROYES',
    name: 'Bijouterie COIFFARD',
    address: '4, rue Raymond Poincarré',
    postalCode: '10000 TROYES',
    phone: '03.25.73.19.24',
    website: 'https://www.coiffard-bijouterie.fr/',
    lat: 48.2974,
    lng: 4.0744,
    country: 'France',
  },
  {
    id: 7,
    city: 'TOKYO',
    name: 'MUSE DEUXIEME CLASSE',
    address: '',
    postalCode: 'JAPON',
    phone: '',
    website: 'https://muse.deuxieme-classe.jp/',
    lat: 35.6762,
    lng: 139.6503,
    country: 'Japon',
  },
];

const PointsDeVentePage: React.FC = () => {
  const [selectedStore, setSelectedStore] = useState<number | null>(null);
  const markerRefs = useRef<{ [key: number]: any }>({});
  const mapRef = useRef<HTMLDivElement>(null);

  // Centre de la carte sur la France
  const mapCenter: [number, number] = [46.603354, 1.888334];

  const handleStoreClick = (storeId: number) => {
    setSelectedStore(storeId);
    
    // Scroll vers la carte avec un offset pour le header
    if (mapRef.current) {
      const headerHeight = 96; // Hauteur du header sticky (h-24 = 96px)
      const extraSpace = 40; // Espace supplémentaire au-dessus de la carte
      const element = mapRef.current;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerHeight - extraSpace;
      
      window.scrollTo({ 
        top: Math.max(0, offsetPosition), // S'assurer de ne pas scroller en négatif
        behavior: 'smooth' 
      });
    }

    // Ouvrir le popup du marqueur (via react-leaflet)
    setTimeout(() => {
      const markerRef = markerRefs.current[storeId];
      if (markerRef) {
        // Accéder au marker Leaflet sous-jacent
        const leafletMarker = markerRef as any;
        if (leafletMarker && typeof leafletMarker.openPopup === 'function') {
          leafletMarker.openPopup();
        }
      }
    }, 800);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <img 
          src="/assets/images/bague-or-18-carats-diamants-aupiho-joaillerie.jpeg"
          alt="Bague en or 18 carats avec diamants - Aupiho Joaillerie"
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ position: 'fixed' }}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-light text-white mb-6 tracking-wide" style={{ fontFamily: 'MoonTime, cursive' }}>
              Nos Points de Vente
            </h1>
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-px bg-[#c69c70]"></div>
              <SparklesIcon className="h-8 w-8 text-[#c69c70]" />
              <div className="w-12 h-px bg-[#c69c70]"></div>
            </div>
            <p className="text-xl text-white/90 font-light max-w-2xl mx-auto">
              Retrouvez nos créations dans nos boutiques partenaires en France et au Japon
            </p>
          </div>
        </div>
      </section>

      {/* Map and Stores Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Carte interactive */}
            <div className="order-2 lg:order-1" ref={mapRef}>
              <div className="sticky top-28">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-[#c69c70]/20">
                  <div className="h-[600px] relative">
                    <MapContainer
                      center={mapCenter}
                      zoom={6}
                      style={{ height: '100%', width: '100%' }}
                      scrollWheelZoom={false}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      {stores.map((store) => (
                        <Marker
                          key={store.id}
                          position={[store.lat, store.lng]}
                          ref={(ref) => {
                            if (ref) {
                              markerRefs.current[store.id] = ref;
                            }
                          }}
                          eventHandlers={{
                            click: () => setSelectedStore(store.id),
                          }}
                        >
                          <Popup>
                            <div className="p-2 min-w-[200px]">
                              <h3 className="font-bold text-[#497aab] mb-1 text-base">{store.name}</h3>
                              {store.address && (
                                <p className="text-sm text-gray-600 mb-1">{store.address}</p>
                              )}
                              <p className="text-sm text-gray-600 mb-2">{store.postalCode}</p>
                              {store.phone && (
                                <p className="text-sm text-[#c69c70] font-medium mb-2">
                                  <PhoneIcon className="inline h-3 w-3 mr-1" />
                                  {store.phone}
                                </p>
                              )}
                              {store.website && (
                                <a
                                  href={store.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-sm text-[#497aab] hover:text-[#c69c70] font-medium transition-colors"
                                >
                                  <GlobeAltIcon className="h-4 w-4" />
                                  Visiter le site
                                </a>
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                    </MapContainer>
                  </div>
                </div>
              </div>
            </div>

            {/* Liste des boutiques */}
            <div className="order-1 lg:order-2 space-y-6">
              <div className="mb-8">
                <h2 className="text-3xl font-light text-[#497aab] mb-2" style={{ fontFamily: 'Gelion, sans-serif' }}>Nos Boutiques Partenaires</h2>
                <div className="h-1 w-16 bg-[#c69c70]"></div>
              </div>

              {stores.map((store) => (
                <div
                  key={store.id}
                  className={`group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border-2 cursor-pointer ${
                    selectedStore === store.id
                      ? 'border-[#c69c70] scale-105'
                      : 'border-transparent hover:border-[#c69c70]/30'
                  }`}
                  onClick={() => handleStoreClick(store.id)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-[#497aab] mb-1 group-hover:text-[#c69c70] transition-colors">
                        {store.city}
                      </h3>
                      <p className="text-lg text-gray-800 font-medium">{store.name}</p>
                    </div>
                    <div className={`p-2 rounded-full transition-all duration-300 ${
                      selectedStore === store.id
                        ? 'bg-[#c69c70]'
                        : 'bg-gray-100 group-hover:bg-[#c69c70]'
                    }`}>
                      <MapPinIcon className={`h-6 w-6 transition-colors ${
                        selectedStore === store.id
                          ? 'text-white'
                          : 'text-gray-600 group-hover:text-white'
                      }`} />
                    </div>
                  </div>

                  <div className="space-y-2 text-gray-600">
                    {store.address && (
                      <p className="flex items-start gap-2">
                        <span className="text-[#c69c70] mt-1">•</span>
                        <span>{store.address}</span>
                      </p>
                    )}
                    <p className="flex items-start gap-2">
                      <span className="text-[#c69c70] mt-1">•</span>
                      <span>{store.postalCode}</span>
                    </p>
                    {store.phone && (
                      <p className="flex items-center gap-2 text-[#c69c70] font-medium">
                        <PhoneIcon className="h-4 w-4" />
                        <a 
                          href={`tel:${store.phone.replace(/\./g, '')}`} 
                          className="hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {store.phone}
                        </a>
                      </p>
                    )}
                    {store.website && (
                      <p className="flex items-center gap-2 text-[#497aab] font-medium">
                        <GlobeAltIcon className="h-4 w-4" />
                        <a 
                          href={store.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:text-[#c69c70] hover:underline transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Visiter le site web
                        </a>
                      </p>
                    )}
                  </div>

                  {store.country === 'Japon' && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <span className="inline-block px-3 py-1 bg-[#497aab]/10 text-[#497aab] rounded-full text-sm font-medium">
                        🇯🇵 International
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#497aab] to-[#5a8bc4]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
              Vous souhaitez devenir partenaire ?
            </h2>
            <p className="text-xl text-white/90 mb-8 font-light">
              Contactez-nous pour distribuer nos créations dans votre boutique
            </p>
            <a
              href="/contact"
              className="inline-block px-10 py-4 bg-[#c69c70] text-white hover:bg-[#b88a5f] transition-all duration-300 font-light tracking-wider text-lg shadow-xl hover:shadow-2xl rounded-full"
            >
              NOUS CONTACTER
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PointsDeVentePage;

